import { useEffect, useRef, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  off,
  onValue,
  ref,
  query,
  DatabaseReference,
  Query,
  QueryConstraint,
  limitToLast,
} from "firebase/database";
import { firebaseDb } from "../firebase/app";

interface UseFirebaseRealtimeQueryOptions<T> {
  queryKey: readonly unknown[];
  path: string | null;
  enabled?: boolean;
  transform?: (data: any) => T;
  defaultValue?: T;
  filters?: QueryConstraint[];
  limitToLast?: number;
}

/**
 * Hook that integrates Firebase real-time database listeners with React Query.
 * This provides real-time updates (socket-like behavior) while leveraging
 * React Query's caching to prevent unnecessary refetches on re-renders.
 *
 * The listener is set up once and updates the React Query cache in real-time.
 * When the component remounts, cached data is shown immediately.
 */
export const useFirebaseRealtimeQuery = <T = any>({
  queryKey,
  path,
  enabled = true,
  transform,
  defaultValue,
  filters,
  limitToLast: limitToLastCount,
}: UseFirebaseRealtimeQueryOptions<T>) => {
  const queryClient = useQueryClient();
  const listenerRef = useRef<DatabaseReference | Query | null>(null);
  const isListenerSetupRef = useRef(false);

  // Get cached data immediately if available
  const cachedData = queryClient.getQueryData<T>(queryKey);

  // Infer default value: if transform exists and returns array for empty input, use []
  // Otherwise use provided defaultValue or try to infer from transform
  const inferredDefault = useMemo<T>(() => {
    if (defaultValue !== undefined) {
      return defaultValue;
    } else if (transform) {
      try {
        const testResult = transform({});
        return (Array.isArray(testResult) ? ([] as T) : testResult) as T;
      } catch {
        // If transform throws on empty object, default to empty array if transform typically returns arrays
        return [] as T;
      }
    } else {
      // No transform and no defaultValue - default to false for boolean-like queries
      // This is a best guess, but defaultValue should be provided for non-array types
      return false as T;
    }
  }, [defaultValue, transform]);

  // Set up the query with a placeholder function (won't be called due to staleTime: Infinity)
  const { data, isLoading, isError, error } = useQuery<T>({
    queryKey,
    queryFn: async () => {
      // This should not be called due to staleTime: Infinity
      // Return cached data or inferred default (React Query doesn't allow undefined)
      return (cachedData ?? inferredDefault) as T;
    },
    enabled: enabled && !!path,
    staleTime: Infinity, // Data is always fresh since it's real-time
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    initialData: cachedData ?? inferredDefault, // Use cached data or inferred default
    placeholderData: (previousData) => previousData,
  });

  // Set up the Firebase listener in useEffect (runs once per path change)
  useEffect(() => {
    if (!enabled || !path) {
      // Clean up if disabled or no path
      if (listenerRef.current) {
        off(listenerRef.current);
        listenerRef.current = null;
        isListenerSetupRef.current = false;
      }
      return;
    }

    // Clean up existing listener if path changed
    if (listenerRef.current) {
      off(listenerRef.current);
      listenerRef.current = null;
      isListenerSetupRef.current = false;
    }

    // Create database reference with optional filters and limitToLast
    const allFilters: QueryConstraint[] = [...(filters || [])];
    if (limitToLastCount !== undefined) {
      allFilters.push(limitToLast(limitToLastCount));
    }

    const dbRef =
      allFilters.length > 0 ? query(ref(firebaseDb, path), ...allFilters) : ref(firebaseDb, path);
    listenerRef.current = dbRef;
    isListenerSetupRef.current = true;

    // Set up the real-time listener
    onValue(
      dbRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const rawData = snapshot.val();
          const transformedData = transform ? transform(rawData) : rawData;

          // Update the cache with new data in real-time
          queryClient.setQueryData<T>(queryKey, transformedData);
        } else {
          // No data exists - use the inferred default value (React Query doesn't allow undefined)
          queryClient.setQueryData<T>(queryKey, inferredDefault);
        }
      },
      (error) => {
        console.error("Firebase listener error:", error);
        // Note: For real-time listeners, we log errors but don't set query errors
        // as the listener will continue to work and update the cache when data is available
      }
    );

    // Cleanup function
    return () => {
      if (listenerRef.current) {
        off(listenerRef.current);
        listenerRef.current = null;
        isListenerSetupRef.current = false;
      }
    };
  }, [
    path,
    enabled,
    queryKey,
    transform,
    queryClient,
    defaultValue,
    inferredDefault,
    filters,
    limitToLastCount,
  ]);

  return {
    data: data ?? cachedData,
    isLoading: isLoading && !cachedData,
    isError,
    error,
  };
};
